import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  MoreVert,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  isToday,
  parseISO,
  addDays,
  isAfter,
} from 'date-fns';
import { updateAppointmentStatus, rescheduleAppointment } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

interface Appointment {
  id: number;
  client_name: string;
  service_name: string;
  date: string;
  time: string;
  status: string;
}

type AppointmentStatus = 'completed' | 'cancelled';

interface AppointmentCalendarProps {
  appointments: Appointment[];
  onStatusUpdate?: () => void;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({ appointments, onStatusUpdate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState<AppointmentStatus | ''>('');
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');
  const [rescheduleErrors, setRescheduleErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery('(max-width:400px)');
  const { showNotification } = useNotification();

  const onDateClick = (day: Date) => {
    setSelectedDate(day);
    if (isMobile) {
      setDrawerOpen(true);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, appointment: Appointment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusUpdate = (status: AppointmentStatus) => {
    handleMenuClose();
    setStatusToUpdate(status);
    setConfirmDialogOpen(true);
  };

  const handleRescheduleClick = () => {
    handleMenuClose();
    if (selectedAppointment) {
      setNewDate(selectedAppointment.date);
      setNewTime(selectedAppointment.time);
      setRescheduleDialogOpen(true);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    if (selectedAppointment && statusToUpdate) {
      setLoading(true);
      try {
        await updateAppointmentStatus(selectedAppointment.id, statusToUpdate);
        if (onStatusUpdate) {
          onStatusUpdate();
        }
        if (showNotification) {
          showNotification(`Appointment ${statusToUpdate === 'completed' ? 'completed' : 'cancelled'} successfully`, 'success');
        }
      } catch (error) {
        console.error('Error updating appointment status:', error);
        if (showNotification) {
          showNotification(`Failed to update appointment status`, 'error');
        }
      } finally {
        setLoading(false);
      }
    }
    setConfirmDialogOpen(false);
    setSelectedAppointment(null);
    setStatusToUpdate('');
  };

  const handleCancelStatusUpdate = () => {
    setConfirmDialogOpen(false);
    setSelectedAppointment(null);
    setStatusToUpdate('');
  };

  const handleRescheduleDialogClose = () => {
    setRescheduleDialogOpen(false);
    setDateError('');
    setTimeError('');
  };

  const validateRescheduleForm = () => {
    let isValid = true;
    const errors: { [key: string]: string } = {};

    if (!newDate) {
      errors.date = 'Date is required';
      isValid = false;
    } else {
      // Check if date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(newDate);
      if (selectedDate < today) {
        errors.date = 'Cannot schedule for a past date';
        isValid = false;
      }
    }

    if (!newTime) {
      errors.time = 'Time is required';
      isValid = false;
    }

    setRescheduleErrors(errors);
    setDateError(errors.date || '');
    setTimeError(errors.time || '');

    if (!isValid && showNotification) {
      showNotification('Please fill in all required fields', 'error');
    }

    return isValid;
  };

  const handleConfirmReschedule = async () => {
    if (!validateRescheduleForm() || !selectedAppointment) {
      return;
    }

    setLoading(true);
    try {
      await rescheduleAppointment(selectedAppointment.id, newDate, newTime);
      if (onStatusUpdate) {
        onStatusUpdate();
      }
      handleRescheduleDialogClose();
      setSelectedAppointment(null);
      
      if (showNotification) {
        showNotification('Appointment rescheduled successfully', 'success');
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      if (showNotification) {
        showNotification('Failed to reschedule appointment', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate time slots for the select dropdown
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 19; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        const time = `${formattedHour}:${formattedMinute}:00`;
        const displayTime = format(parseISO(`2023-01-01T${time}`), 'h:mm a');
        slots.push({ value: time, label: displayTime });
      }
    }
    return slots;
  };

  const renderHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          px: 1,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <IconButton 
          onClick={prevMonth}
          sx={{ 
            width: 36, 
            height: 36,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <ChevronLeft fontSize={isVerySmallScreen ? "small" : "medium"} />
        </IconButton>
        <Typography 
          variant={isVerySmallScreen ? "subtitle1" : "h6"} 
          sx={{ 
            fontWeight: 500,
            textAlign: 'center',
            flexGrow: 1,
          }}
        >
          {format(currentMonth, 'MMMM yyyy')}
        </Typography>
        <IconButton 
          onClick={nextMonth}
          sx={{ 
            width: 36, 
            height: 36,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <ChevronRight fontSize={isVerySmallScreen ? "small" : "medium"} />
        </IconButton>
      </Box>
    );
  };

  const renderDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <Grid container spacing={0} sx={{ mb: 1, display: { xs: 'none', sm: 'flex' }, width: '100%' }}>
        {days.map((day) => (
          <Grid item xs key={day} sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  // For mobile, show abbreviated day names at the top
  const renderMobileDays = () => {
    const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return (
      <Grid container spacing={0} sx={{ mb: 1, display: { xs: 'flex', sm: 'none' }, width: '100%' }}>
        {days.map((day) => (
          <Grid item xs key={day} sx={{ textAlign: 'center' }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 500, 
                color: 'text.secondary',
                fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = monthStart;
    const endDate = monthEnd;

    const dateFormat = 'd';
    const rows = [];

    let days = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });

    // Get the day of the week for the first day of the month (0-6)
    const startDay = getDay(monthStart);
    
    // Add empty cells for days before the first day of the month
    const emptyCellsBefore = Array(startDay).fill(null);
    
    // Calculate how many days to add after the last day of the month
    // to complete the last row (if needed)
    const daysInMonth = days.length;
    const totalCells = Math.ceil((daysInMonth + startDay) / 7) * 7;
    const emptyCellsAfter = Array(totalCells - daysInMonth - startDay).fill(null);
    
    // Combine empty cells before, days of the month, and empty cells after
    const allCells = [...emptyCellsBefore, ...days, ...emptyCellsAfter];
    
    // Group cells into rows (7 cells per row)
    for (let i = 0; i < allCells.length; i += 7) {
      rows.push(allCells.slice(i, i + 7));
    }

    return (
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {rows.map((row, rowIndex) => (
          <Grid 
            container 
            key={rowIndex} 
            sx={{ 
              mb: { xs: 1, sm: 2 },
              width: '100%',
              mx: 'auto',
              justifyContent: 'space-between',
            }} 
            spacing={0}
          >
            {row.map((day, dayIndex) => {
              if (!day) {
                // Empty cell
                return (
                  <Grid 
                    item 
                    xs 
                    key={`empty-${dayIndex}`} 
                    sx={{ 
                      height: { 
                        xs: isVerySmallScreen ? 40 : 50, 
                        sm: 70 
                      },
                      padding: '2px',
                      flexBasis: 'calc(100% / 7)',
                      maxWidth: 'calc(100% / 7)',
                    }} 
                  />
                );
              }

              // Filter appointments for this day
              const dayAppointments = appointments.filter((appointment) => {
                const appointmentDate = parseISO(`${appointment.date}T${appointment.time}`);
                return isSameDay(appointmentDate, day);
              });

              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isTodayDate = isToday(day);

              return (
                <Grid 
                  item 
                  xs 
                  key={format(day, 'T')}
                  sx={{ 
                    padding: '2px',
                    flexBasis: 'calc(100% / 7)',
                    maxWidth: 'calc(100% / 7)',
                  }}
                >
                  <Paper
                    elevation={isSelected ? 3 : 0}
                    sx={{
                      height: { 
                        xs: isVerySmallScreen ? 40 : 50, 
                        sm: 70 
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? 'primary.light'
                        : isTodayDate
                        ? 'secondary.light'
                        : theme.palette.background.paper,
                      opacity: isCurrentMonth ? 1 : 0.5,
                      border: isTodayDate ? '2px solid' : '1px solid',
                      borderColor: isTodayDate ? theme.palette.primary.main : theme.palette.divider,
                      borderRadius: isVerySmallScreen ? 1 : 2,
                      boxShadow: isSelected 
                        ? `0 4px 8px ${theme.palette.primary.light}`
                        : 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        transform: 'scale(1.02)',
                        transition: 'transform 0.2s',
                      },
                      transition: 'all 0.2s',
                      p: isVerySmallScreen ? 0.5 : 1,
                    }}
                    onClick={() => onDateClick(day)}
                  >
                    <Typography
                      variant={isVerySmallScreen ? "caption" : (isMobile ? "body2" : "body1")}
                      sx={{
                        fontWeight: isTodayDate ? 'bold' : 'normal',
                        mb: isVerySmallScreen ? 0 : 0.5,
                        fontSize: isVerySmallScreen ? '0.7rem' : undefined,
                        color: isSelected 
                          ? theme.palette.primary.dark
                          : theme.palette.text.primary,
                      }}
                    >
                      {format(day, dateFormat)}
                    </Typography>
                    {dayAppointments.length > 0 && (
                      <Badge
                        badgeContent={dayAppointments.length}
                        color="primary"
                        sx={{ 
                          '& .MuiBadge-badge': {
                            fontSize: isVerySmallScreen ? '0.6rem' : '0.7rem',
                            height: isVerySmallScreen ? 14 : 18,
                            minWidth: isVerySmallScreen ? 14 : 18,
                            padding: isVerySmallScreen ? '0 2px' : '0 4px',
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                          }
                        }}
                      >
                        <CalendarToday 
                          sx={{ 
                            fontSize: isVerySmallScreen ? '0.8rem' : (isMobile ? '1rem' : '1.2rem'),
                            color: isSelected ? theme.palette.primary.dark : theme.palette.primary.main
                          }} 
                        />
                      </Badge>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>
    );
  };

  const renderAppointmentsForSelectedDate = () => {
    if (!selectedDate) return null;

    const selectedAppointments = appointments.filter((appointment) => {
      const appointmentDate = parseISO(`${appointment.date}T${appointment.time}`);
      return isSameDay(appointmentDate, selectedDate);
    });

    if (selectedAppointments.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">No appointments for this day.</Typography>
        </Box>
      );
    }

    return (
      <List sx={{ width: '100%' }}>
        {selectedAppointments.map((appointment) => (
          <React.Fragment key={appointment.id}>
            <ListItem 
              alignItems="flex-start" 
              sx={{ 
                py: 1.5,
                pr: isVerySmallScreen ? 6 : 'auto', // Ensure space for the action button
              }}
              secondaryAction={
                <IconButton 
                  edge="end" 
                  aria-label="appointment options"
                  onClick={(e) => handleMenuClick(e, appointment)}
                  disabled={appointment.status === 'completed' || appointment.status === 'cancelled'}
                  size={isVerySmallScreen ? "small" : "medium"}
                >
                  <MoreVert fontSize={isVerySmallScreen ? "small" : "medium"} />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Typography 
                    variant={isVerySmallScreen ? "body2" : "subtitle1"} 
                    sx={{ fontWeight: 500 }}
                  >
                    {appointment.service_name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography 
                      variant={isVerySmallScreen ? "caption" : "body2"} 
                      component="span"
                    >
                      {appointment.client_name} • {format(parseISO(`${appointment.date}T${appointment.time}`), 'h:mm a')}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={appointment.status}
                        size="small"
                        color={
                          appointment.status === 'completed'
                            ? 'success'
                            : appointment.status === 'cancelled'
                            ? 'error'
                            : 'primary'
                        }
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                }
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        mb: 3, 
        width: '100%',
        maxWidth: '100%',
      }}>
        {renderHeader()}
        {renderMobileDays()}
        {renderDays()}
        {renderCells()}
      </Box>

      {!isMobile && selectedDate && (
        <Paper 
          sx={{ 
            p: 3, 
            mt: 3, 
            borderRadius: 2, 
            width: '100%',
            background: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${theme.palette.primary.light}`,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.primary }}>
            Appointments for {format(selectedDate, 'MMMM d, yyyy')}
          </Typography>
          {renderAppointmentsForSelectedDate()}
        </Paper>
      )}

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            px: isVerySmallScreen ? 1 : 2,
            pt: 3,
            pb: 2,
            background: theme.palette.background.paper,
            boxShadow: `0 -4px 16px ${theme.palette.primary.light}`,
          },
        }}
      >
        <Box sx={{ 
          width: '40px', 
          height: '4px', 
          backgroundColor: theme.palette.divider, 
          borderRadius: '4px',
          margin: '0 auto 16px auto'
        }} />
        {selectedDate && (
          <>
            <Typography 
              variant={isVerySmallScreen ? "subtitle1" : "h6"} 
              sx={{ 
                mb: 2, 
                fontWeight: 500,
                px: isVerySmallScreen ? 1 : 0,
                color: theme.palette.text.primary,
              }}
            >
              Appointments for {format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            {renderAppointmentsForSelectedDate()}
          </>
        )}
      </Drawer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { 
            width: isVerySmallScreen ? 180 : 200, 
            mt: 0.5,
            background: theme.palette.background.paper,
          }
        }}
      >
        <MenuItem 
          onClick={handleRescheduleClick}
          sx={{ 
            justifyContent: isVerySmallScreen ? 'center' : 'flex-start',
            py: isVerySmallScreen ? 1.5 : 1,
            px: isVerySmallScreen ? 2 : 1,
            minHeight: isVerySmallScreen ? '44px' : 'auto',
          }}
          disabled={selectedAppointment?.status === 'completed' || selectedAppointment?.status === 'cancelled'}
        >
          <Typography 
            color="primary"
            variant={isVerySmallScreen ? "body2" : "body1"}
            align="center"
            sx={{ 
              width: '100%',
              textAlign: isVerySmallScreen ? 'center' : 'left',
              fontWeight: 400,
            }}
          >
            Reschedule
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusUpdate('completed')}
          sx={{ 
            justifyContent: isVerySmallScreen ? 'center' : 'flex-start',
            py: isVerySmallScreen ? 1.5 : 1,
            px: isVerySmallScreen ? 2 : 1,
            minHeight: isVerySmallScreen ? '44px' : 'auto',
          }}
        >
          <Typography 
            color="success.main"
            variant={isVerySmallScreen ? "body2" : "body1"}
            align="center"
            sx={{ 
              width: '100%',
              textAlign: isVerySmallScreen ? 'center' : 'left',
              fontWeight: 400,
            }}
          >
            Mark as Completed
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusUpdate('cancelled')}
          sx={{ 
            justifyContent: isVerySmallScreen ? 'center' : 'flex-start',
            py: isVerySmallScreen ? 1.5 : 1,
            px: isVerySmallScreen ? 2 : 1,
            minHeight: isVerySmallScreen ? '44px' : 'auto',
          }}
        >
          <Typography 
            color="error.main"
            variant={isVerySmallScreen ? "body2" : "body1"}
            align="center"
            sx={{ 
              width: '100%',
              textAlign: isVerySmallScreen ? 'center' : 'left',
              fontWeight: 400,
            }}
          >
            Cancel Appointment
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelStatusUpdate}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            width: isVerySmallScreen ? '90%' : 'auto',
            maxWidth: isVerySmallScreen ? '90%' : 'sm',
            background: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${theme.palette.primary.light}`,
            borderRadius: isMobile ? 3 : 2,
            p: isMobile ? 1 : 0,
            position: 'relative',
          }
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <DialogTitle id="alert-dialog-title" sx={{ 
          color: theme.palette.text.primary,
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          pt: isMobile ? 2 : 3,
          pb: isMobile ? 1 : 2,
        }}>
          {statusToUpdate === 'completed' ? 'Complete Appointment' : 'Cancel Appointment'}
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <DialogContentText id="alert-dialog-description" sx={{ 
            color: theme.palette.text.secondary,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}>
            {statusToUpdate === 'completed'
              ? 'Are you sure you want to mark this appointment as completed?'
              : 'Are you sure you want to cancel this appointment?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          pb: isMobile ? 2 : 2,
          pt: isMobile ? 1 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isVerySmallScreen ? 'column' : 'row',
          gap: isVerySmallScreen ? 1 : 0,
        }}>
          <Button 
            onClick={handleCancelStatusUpdate} 
            color="primary"
            variant="outlined"
            fullWidth={isVerySmallScreen}
            disabled={loading}
            sx={{ 
              minWidth: isMobile && !isVerySmallScreen ? '45%' : '80px',
              fontSize: isMobile ? '0.85rem' : '0.875rem',
              mb: isVerySmallScreen ? 1 : 0,
            }}
          >
            No
          </Button>
          <Button 
            onClick={handleConfirmStatusUpdate} 
            color="primary" 
            variant="contained"
            fullWidth={isVerySmallScreen}
            disabled={loading}
            autoFocus
            sx={{ 
              minWidth: isMobile && !isVerySmallScreen ? '45%' : '80px',
              fontSize: isMobile ? '0.85rem' : '0.875rem',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </Box>
            ) : (
              'Yes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rescheduleDialogOpen}
        onClose={handleRescheduleDialogClose}
        aria-labelledby="reschedule-dialog-title"
        PaperProps={{
          sx: {
            width: isVerySmallScreen ? '90%' : 'auto',
            maxWidth: isVerySmallScreen ? '90%' : 'sm',
            background: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${theme.palette.primary.light}`,
            borderRadius: isMobile ? 3 : 2,
            p: isMobile ? 1 : 0,
            position: 'relative',
          }
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <DialogTitle id="reschedule-dialog-title" sx={{ 
          color: theme.palette.text.primary,
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          pt: isMobile ? 2 : 3,
          pb: isMobile ? 1 : 2,
        }}>
          Reschedule Appointment
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <DialogContentText sx={{ 
            color: theme.palette.text.secondary,
            fontSize: isMobile ? '0.9rem' : '1rem',
            mb: 2,
          }}>
            {selectedAppointment && `Reschedule appointment for ${selectedAppointment.client_name}`}
          </DialogContentText>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2,
            mt: 1,
          }}>
            <TextField
              label="Date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!dateError}
              helperText={dateError}
              inputProps={{
                min: format(new Date(), 'yyyy-MM-dd'),
              }}
            />
            
            <FormControl fullWidth error={!!timeError}>
              <InputLabel id="time-select-label" shrink>Time</InputLabel>
              <Select
                labelId="time-select-label"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                label="Time"
                displayEmpty
                sx={{ textAlign: 'left' }}
              >
                <MenuItem value="" disabled>
                  <em>Select a time</em>
                </MenuItem>
                {generateTimeSlots().map((slot) => (
                  <MenuItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </MenuItem>
                ))}
              </Select>
              {timeError && <FormHelperText>{timeError}</FormHelperText>}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          pb: isMobile ? 2 : 2,
          pt: isMobile ? 1 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isVerySmallScreen ? 'column' : 'row',
          gap: isVerySmallScreen ? 1 : 0,
        }}>
          <Button 
            onClick={handleRescheduleDialogClose} 
            color="primary"
            variant="outlined"
            fullWidth={isVerySmallScreen}
            disabled={loading}
            sx={{ 
              minWidth: isMobile && !isVerySmallScreen ? '45%' : '80px',
              fontSize: isMobile ? '0.85rem' : '0.875rem',
              mb: isVerySmallScreen ? 1 : 0,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmReschedule} 
            color="primary" 
            variant="contained"
            fullWidth={isVerySmallScreen}
            disabled={loading}
            sx={{ 
              minWidth: isMobile && !isVerySmallScreen ? '45%' : '80px',
              fontSize: isMobile ? '0.85rem' : '0.875rem',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </Box>
            ) : (
              'Reschedule'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentCalendar; 